import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { connectionsApi } from '@/core/api/connections.api'
import { invalidateConnections } from '@/lib/query.client'
import { useToast } from '@/shared/components/toast'
import { Logo, StatusBadge, relativeTime } from '@/shared/components'
import type { Connection } from '@/core/types/api.types'
import { useConnectionHealth } from './useConnection'
import { useState } from 'react'

export function ConnectionCard({ connection }: { connection: Connection }) {
  const toast = useToast()
  const [checking, setChecking] = useState(false)
  const health = useConnectionHealth(connection.connection_id, checking)

  const revoke = useMutation({
    mutationFn: () => connectionsApi.revoke(connection.connection_id),
    onSuccess: async () => {
      await invalidateConnections()
      toast.success('Disconnected', `${connection.label} was removed.`)
    },
    onError: (err) => toast.error(err, 'Could not disconnect'),
  })

  const identity =
    typeof connection.metadata?.identity === 'string'
      ? (connection.metadata.identity as string)
      : null

  return (
    <div className="card stack" style={{ gap: 11 }}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <Logo
          src={connection.connector_logo}
          name={connection.connector_name ?? connection.connector_id}
          id={connection.connector_id}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link
            to={`/connections/${connection.connection_id}`}
            style={{ fontWeight: 600, color: 'var(--text)' }}
          >
            {connection.label}
          </Link>
          <div className="small muted truncate">
            {connection.connector_name ?? connection.connector_id}
            {identity && ` · ${identity}`}
          </div>
        </div>
        <StatusBadge status={health.data?.status ?? connection.status} />
      </div>

      {connection.last_error && (
        <p className="small" style={{ margin: 0, color: 'var(--danger)' }}>
          {connection.last_error}
        </p>
      )}

      {health.data && health.data.status === 'active' && (
        <p className="small" style={{ margin: 0, color: 'var(--ok)' }}>
          {health.data.message}
          {health.data.identity ? ` (${health.data.identity})` : ''}
        </p>
      )}

      <div className="row small faint" style={{ gap: 12 }}>
        <span>Connected {relativeTime(connection.created_at)}</span>
        <span className="badge badge-neutral">{connection.auth_type}</span>
        {connection.scopes.length > 0 && (
          <span title={connection.scopes.join(', ')}>{connection.scopes.length} scopes</span>
        )}
      </div>

      <div className="row">
        <Link className="btn btn-sm btn-primary" to={`/run?connection=${connection.connection_id}`}>
          Run an action
        </Link>
        <button
          className="btn btn-sm"
          disabled={health.isFetching}
          onClick={() => {
            setChecking(true)
            health.refetch()
          }}
        >
          {health.isFetching ? 'Checking…' : 'Test'}
        </button>
        <div className="spacer" />
        <button
          className="btn btn-sm btn-danger"
          disabled={revoke.isPending}
          onClick={() => {
            // A revoke destroys the stored credential and cannot be undone —
            // worth one confirmation.
            if (window.confirm(`Disconnect "${connection.label}"? This deletes its stored credentials.`)) {
              revoke.mutate()
            }
          }}
        >
          {revoke.isPending ? 'Removing…' : 'Disconnect'}
        </button>
      </div>
    </div>
  )
}
